import { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import HomiPage from "./pages/HomiPage";
import AboutPage from "./pages/AboutPage";
import ServicesPage from "./pages/ServicesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ContactPage from "./pages/ContactPage";
import ServiceDetailPage from "./pages/ServiceDetailPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import BlogPage from "./pages/BlogPage";
import BlogDetailPage from "./pages/BlogDetailPage";
import AdminLogin from "./pages/admin/AdminLogin";
import AdminDashboard from "./pages/admin/AdminDashboard";
import { Toaster } from "./components/ui/toaster";
import WhatsAppFloat from "./components/WhatsAppFloat";
import ErrorBoundary from "./components/ErrorBoundary";
import { ContentProvider } from "./context/ContentContext";
import { AdminAuthProvider, useAdminAuth } from "./context/AdminAuthContext";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function FloatingButtons() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin")) return null;
  return <WhatsAppFloat />;
}

function RequireAdmin({ children }) {
  const { admin, checking } = useAdminAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-homi-light">
        <Loader2 className="w-8 h-8 animate-spin text-homi-gold" />
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin" replace />;
  return children;
}

function RedirectIfAuthed({ children }) {
  const { admin, checking } = useAdminAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-homi-black">
        <Loader2 className="w-8 h-8 animate-spin text-homi-gold" />
      </div>
    );
  }
  if (admin) return <Navigate to="/admin/dashboard" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <ContentProvider>
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Navigate to="/homi" replace />} />
              <Route path="/homi" element={<HomiPage />} />
              <Route path="/homi/about" element={<AboutPage />} />
              <Route path="/homi/services" element={<ServicesPage />} />
              <Route path="/homi/services/:slug" element={<ServiceDetailPage />} />
              <Route path="/homi/projects" element={<ProjectsPage />} />
              <Route path="/homi/projects/:id" element={<ProjectDetailPage />} />
              <Route path="/homi/blog" element={<BlogPage />} />
              <Route path="/homi/blog/:slug" element={<BlogDetailPage />} />
              <Route path="/homi/contact" element={<ContactPage />} />

              {/* Admin */}
              <Route
                path="/admin"
                element={
                  <AdminAuthProvider>
                    <RedirectIfAuthed>
                      <AdminLogin />
                    </RedirectIfAuthed>
                  </AdminAuthProvider>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <AdminAuthProvider>
                    <RequireAdmin>
                      <AdminDashboard />
                    </RequireAdmin>
                  </AdminAuthProvider>
                }
              />

              {/* Any other sub-path falls back to home so no nav link breaks. */}
              <Route path="/homi/*" element={<HomiPage />} />
            </Routes>
            <FloatingButtons />
          </BrowserRouter>
          <Toaster />
        </ContentProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
